import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { ArticleStoreService } from 'src/app/services/article/article-store.service';
import { ArticleListTableTabs, SEOFilterOptions, SEOFilterRange } from 'src/app/_shared/consts/article-constants';
import { ArticleHelperService } from 'src/app/_shared/services/helper/article/article-helper.service';
import { ArticleService } from 'src/app/services/article/article.service';
import { ArticleDetail, GetArticleListResponse, GetArticleWidgetStatsResponse, GetSEOWidgetResponse, SEOWidgetStoreStateType, Stats } from 'src/app/_shared/models/article';
import { StorageHelper } from 'src/app/_utilities/storage-helper';
import { CampaignStats } from 'src/app/_shared/models/campaigns';

@Component({
  selector: 'app-article-performance-review',
  templateUrl: './article-performance-review.component.html',
  styleUrls: ['./article-performance-review.component.scss']
})
export class ArticlePerformanceReviewComponent {

  // 'article' shows Published/Draft/Scheduled/Avg. Score cards fed by the article widget stats API.
  // 'campaign' has no SEO score concept, so it shows Ready/In Progress/In Queue/Total Projects instead.
  @Input() context: 'article' | 'campaign' = 'article';

  // Campaign context only: counts loaded by the parent campaigns table (no API calls made here)
  @Input() set campaignStats(stats: CampaignStats | null) {
    if (stats) {
      Object.assign(this, stats);
    }
  }

  getBlogWidgetSubscription!: Subscription;
  articleListSubscription!: Subscription;
  widgetStatsRefreshSubscription!: Subscription;

  public SEOFilterOptions = SEOFilterOptions;
  public listTableTabs = ArticleListTableTabs;

  statusFormat = (percent: number): string => `${percent}%`;
  _storageHelper: StorageHelper;

  // Dynamic counts
  publishedCount: number = 0;
  draftCount: number = 0;
  scheduledCount: number = 0;
  avgSeoScore: number = 0;

  // "Last 7 days" figures, bound from the widget stats API (server-computed)
  publishedLast7: number = 0;
  draftLast7: number = 0;
  scheduledLast7: number = 0;
  avgSeoLast7: number = 0;

  // Campaign context counts, set from the campaignStats input
  // inQueueCount is kept populated but its KPI card is currently hidden from the template.
  readyCount: number = 0;
  inProgressCount: number = 0;
  inQueueCount: number = 0;
  totalProjectsCount: number = 0;

  // Campaign "Last 7 days" figures, set from the campaignStats input
  readyLast7: number = 0;
  inProgressLast7: number = 0;
  inQueueLast7: number = 0;
  totalProjectsLast7: number = 0;

  // Sum of associatedArticlesCount across all projects (the "N items" shown per project row)
  totalItemsCount: number = 0;
  totalItemsLast7: number = 0;

  constructor(
    private cdRef: ChangeDetectorRef,
    private articleService: ArticleService, public articleStoreService: ArticleStoreService,
    private articleHelperService: ArticleHelperService
  ) {

    this._storageHelper = new StorageHelper();
  }
  ngOnInit(): void {
    if (this.context === 'campaign') {
      // Campaign counts come in through the campaignStats input; the parent table owns the API calls.
      return;
    }
    this.loadWidgetStats();
    // Re-fetch KPI stats whenever an article is moved to trash / restored / deleted so the
    // cards (including the "Last 7 days" figures) update automatically without a page refresh.
    this.widgetStatsRefreshSubscription = this.articleStoreService.widgetStatsRefresh$.subscribe(() => {
      this.loadWidgetStats();
    });
  }

  private loadWidgetStats(): void {
    this.articleHelperService.getArticleWidgetStatsData(this._storageHelper.user.pId, (response: GetArticleWidgetStatsResponse) => {
      if (response) {
        // Reset first so a status missing from the response (e.g. no scheduled articles left after a
        // delete) resets its card to 0 instead of keeping the previous count.
        this.publishedCount = this.draftCount = this.scheduledCount = 0;
        this.publishedLast7 = this.draftLast7 = this.scheduledLast7 = 0;
        this.avgSeoScore = response.averageSEOScore || 0;
        this.avgSeoLast7 = response.last7DaysAverageSEOScore || 0;
        (response.stats || []).forEach((stat: Stats) => {
          if (stat.label == "Draft") { this.draftCount = +stat.noOfPosts; this.draftLast7 = +stat.last7DaysCount; }
          if (stat.label == "Published") { this.publishedCount = +stat.noOfPosts; this.publishedLast7 = +stat.last7DaysCount; }
          if (stat.label == "Scheduled") { this.scheduledCount = +stat.noOfPosts; this.scheduledLast7 = +stat.last7DaysCount; }
        })
      }
      this.cdRef.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (this.articleListSubscription) {
      this.articleListSubscription.unsubscribe();
    }
    if (this.getBlogWidgetSubscription) {
      this.getBlogWidgetSubscription.unsubscribe();
    }
    if (this.widgetStatsRefreshSubscription) {
      this.widgetStatsRefreshSubscription.unsubscribe();
    }
  }
  
  //private calculateCounts(articles: ArticleDetail[]): void {
  //  if (!articles || articles.length === 0) {
  //    this.publishedCount = 0;
  //    this.draftCount = 0;
  //    this.scheduledCount = 0;
  //    this.avgSeoScore = 0;
  //    return;
  //  }
    
  //  // Count articles by status: 1=Draft, 2=Published, 3=Scheduled
  //  this.draftCount = articles.filter(a => a.status === 1).length;
  //  this.publishedCount = articles.filter(a => a.status === 2).length;
  //  this.scheduledCount = articles.filter(a => a.status === 3).length;
    
  //  // Calculate average SEO score
  //  const totalSeoScore = articles.reduce((sum, article) => sum + (article.seoScore || 0), 0);
  //  this.avgSeoScore = articles.length > 0 ? Math.round(totalSeoScore / articles.length) : 0;
  //}

  public getTrendDirection(value: number): 'up' | 'down' | 'flat' {
    if (value > 0) return 'up';
    if (value < 0) return 'down';
    return 'flat';
  }
  public onSEOFilterClicked(seoFilterOption: SEOFilterOptions) {
    this.articleStoreService.setSEORange(seoFilterOption);
    // set post loading state to true
    this.articleStoreService.updateIsListLoadingState(true);
    // call list api and get all blogs
    this.articleHelperService.getListOfArticles((response: GetArticleListResponse) => {
    // switch based on SEO Filter Options
      switch (seoFilterOption) {
        case SEOFilterOptions.ALL: {
          // set all 
            this.articleStoreService.updateListState(response.articles,response.totalRecords,true,false);
            break;
        }
        case SEOFilterOptions.POOR: {
          // filter poor once based on seo filter range
          let filteredBlogList = this.filterList(response.articles, SEOFilterRange.POOR.From, SEOFilterRange.POOR.To);
          this.articleStoreService.updateListState(filteredBlogList,response.totalRecords,true,false);
          break;
        }
        case SEOFilterOptions.AVERAGE: {
          // filter poor once based on seo filter range
          let filteredBlogList = this.filterList(response.articles, SEOFilterRange.AVERAGE.From, SEOFilterRange.AVERAGE.To);
          this.articleStoreService.updateListState(filteredBlogList,response.totalRecords,true,false);
          break;
        }
        case SEOFilterOptions.GOOD: {
          // filter poor once based on seo filter range
          let filteredBlogList = this.filterList(response.articles, SEOFilterRange.GOOD.From, SEOFilterRange.GOOD.To);
          this.articleStoreService.updateListState(filteredBlogList,response.totalRecords,true,false);
          break;
        }
        default:
          break;
      }
      // set post loading state to false
      this.articleStoreService.updateIsListLoadingState(false);
    });
  }
  private filterList(list: ArticleDetail[], seoScoreFrom: number, seoScoreTo: number) {
    let filteredList = list.filter((blog) => {
      return blog.seoScore >= seoScoreFrom && blog.seoScore <= seoScoreTo;
    })
    return filteredList;
  }
  getStatusColor(value: number) {
    if (value == null)
      return 'normal';

    switch (true) {
      case (value <=15):
        return 'exception';
      case (value <= 59):
        return 'warning';
      case (value <= 100):
        return 'success';
      default:
        return 'normal';
    }
  }
}
